import React, { useState, useEffect } from 'react';
import { ProductShowcase, bedroomShowcaseItems } from './productShowCase';
import { Gallery, bedroomProducts } from './Gallery';
import { CuratedCollections, bedroomCollections } from './CuratedCollections';
import ScrollingSection from './ScrollingSection';
import ScrollToTop from './ScroollToTop';
import Contact from './Contact';

const slides = [
  {
    id: 1,
    image: 'https://images.pexels.com/photos/164595/pexels-photo-164595.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Modern Bedroom Sanctuary',
    subtitle: 'Transform Your Space',
    description: 'Create the perfect retreat with our contemporary bedroom collection featuring clean lines, premium materials, and thoughtful design details.'
  },
  {
    id: 2,
    image: 'https://images.pexels.com/photos/1454806/pexels-photo-1454806.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Luxury Bedroom Suite',
    subtitle: 'Premium Comfort',
    description: 'Experience unparalleled comfort with our handcrafted bedroom furniture designed to elevate your daily routine and enhance your sleep sanctuary.'
  },
  {
    id: 3,
    image: 'https://images.pexels.com/photos/2029722/pexels-photo-2029722.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Minimalist Bedroom Design',
    subtitle: 'Clean Aesthetics',
    description: 'Embrace simplicity with our minimalist bedroom collection that maximizes space while maintaining the highest standards of comfort and style.'
  },
  {
    id: 4,
    image: 'https://images.pexels.com/photos/1743231/pexels-photo-1743231.jpeg?auto=compress&cs=tinysrgb&w=1920',
    title: 'Cozy Bedroom Retreat',
    subtitle: 'Warm Elegance',
    description: 'Discover the perfect balance of warmth and sophistication with our cozy bedroom collection featuring rich textures and inviting atmospheres.'
  }
];

const bedroomCategories = ['Bed', 'Wardrobe', 'Nightstand', 'Dresser'];

// Contact Modal Component
const ContactModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white shadow-3xl w-full max-w-2xl max-h-[90vh] sm:max-h-[95vh] overflow-y-auto relative rounded-lg">
        <button
          className="absolute top-2 right-3 sm:top-3 sm:right-4 text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl font-bold z-[9999]"
          aria-label="Close modal"
          onClick={onClose}
        >
          &times;
        </button>
        <Contact />
      </div>
    </div>
  );
};

const Bedroom = () => {
  const [showModal, setShowModal] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const openModal = () => setShowModal(true);
  const closeModal = () => setShowModal(false);

  // Preload hero images
  useEffect(() => {
    slides.forEach((slide) => {
      const img = new Image();
      img.src = slide.image;
    });
  }, []);

  useEffect(() => {
    if (isAutoPlaying) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isAutoPlaying]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 3000);
  };

  return (
    <div className="animate-slideUp">
      <ScrollToTop />

      {/* Hero Image Slider */}
      <div className="relative w-full h-[50vh] sm:h-[60vh] md:h-[70vh] lg:h-[80vh] overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/10 to-transparent" />
          </div>
        ))}
        <div className="absolute inset-0 flex items-center justify-start px-4 sm:px-6 lg:px-12 z-20">
          <div className="max-w-2xl text-white">
            <p className="text-xs sm:text-sm lg:text-base font-medium tracking-wider uppercase mb-2 text-yellow-400">
              {slides[currentSlide].subtitle}
            </p>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-4 sm:mb-6">
              {slides[currentSlide].title}
            </h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed mb-6 sm:mb-8 text-gray-50">
              {slides[currentSlide].description}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-6 sm:py-4 sm:px-8 text-sm sm:text-base rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg">
                Explore the Collection
              </button>
              <button onClick={openModal} className="border-2 border-white hover:bg-white hover:text-gray-900 font-semibold py-3 px-6 sm:py-4 sm:px-8 text-sm sm:text-base rounded-lg transition-all duration-300 transform hover:scale-105">
                Book Consultation
              </button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 sm:space-x-3 z-30">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${index === currentSlide
                ? 'bg-yellow-500 scale-125'
                : 'bg-white bg-opacity-50 hover:bg-opacity-80'
                }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Product Showcase */}
      <ProductShowcase title="Bedroom Furniture Collections" items={bedroomShowcaseItems} />



      {/* Gallery Section */}
      <section className="bg-white py-8 sm:py-12 min-h-[600px] sm:min-h-[800px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-6 sm:mb-8 text-center">Bedroom Gallery</h2>
          <div className="min-h-[500px] sm:min-h-[600px]">
            <Gallery
              products={bedroomProducts}
              categories={bedroomCategories}
              autoCycle={true}
              cycleInterval={2000}
            />
          </div>
        </div>
      </section>

      {/* Scrolling Section */}
      <section>
        <ScrollingSection
          sectionContent={
            <section className="relative z-10 bg-black/80 p-4 sm:p-6 md:p-8 lg:p-12 xl:p-16 w-full min-h-screen flex items-center justify-center">
              <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center text-white">
                <div className="space-y-4 sm:space-y-6 text-left">
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif leading-tight">Elegant Bedroom Setup</h2>
                  <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed">
                    Transform your bedroom into a cozy retreat with our premium furniture collection.
                  </p>
                  <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base md:text-lg list-disc list-inside">
                    <li><strong>Bed:</strong> King-size with rich oak wood finish</li>
                    <li><strong>Wardrobe:</strong> Spacious with smooth sliding doors</li>
                    <li><strong>Nightstand:</strong> Minimalist design with ample storage drawers</li>
                    <li><strong>Lighting:</strong> Elegant bedside lamps providing a warm, inviting glow</li>
                  </ul>
                  <button className="mt-4 sm:mt-8 bg-gray-600 text-white px-6 py-2.5 sm:px-8 sm:py-3 text-sm sm:text-base md:text-lg rounded-full font-semibold hover:bg-gray-700 transition-colors duration-300 shadow-lg hover:shadow-xl">
                    Explore More
                  </button>
                </div>
                <img
                  src="https://images.pexels.com/photos/1743231/pexels-photo-1743231.jpeg?auto=compress&cs=tinysrgb&w=800&q=80"
                  alt="Modern Bedroom"
                  className="rounded-xl shadow-lg w-full h-auto object-cover transform hover:scale-105 transition-transform duration-300"
                />
              </div>
            </section>
          }
          imageUrl="https://images.pexels.com/photos/164595/pexels-photo-164595.jpeg?auto=compress&cs=tinysrgb&w=1920"
        />
      </section>

      {/* Curated Collections */}
      <CuratedCollections
        title="Curated Bedroom Styles"
        description="Discover cohesive furniture sets and decor ideas for your home's heart."
        collections={bedroomCollections}
      />

      {/* Contact Modal */}
      <ContactModal isOpen={showModal} onClose={closeModal} />
    </div>
  );
};

export default Bedroom;
